import React, { FunctionComponent, useMemo, useState } from 'react'
import { FormattedDateTimeRange, FormattedMessage } from 'react-intl'
import { LayoutChangeEvent, Platform, ScrollView } from 'react-native'

import { addMinutes } from 'date-fns'
import { BlurView } from 'expo-blur'
import styled, { useTheme } from 'styled-components/native'

import { ButtonModifier } from '../../atoms/baseButton/BaseButton'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { CloseIcon } from '../../atoms/icons/CloseIcon'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import TertiaryIconButton from '../../atoms/tertiaryIconButton/TertiaryIconButton'
import { Title } from '../../atoms/title/Title'
import { CameraVideoControlButton } from '../../atoms/videoControlButton/CameraVideoControlButton'
import { MicVideoControlButton } from '../../atoms/videoControlButton/MicVideoControlButton'
import { SettingsVideoControlButton } from '../../atoms/videoControlButton/SettingsVideoControlButton'
import { JoinSessionParams } from '../../hooks/useZoom/types'
import { useZoom } from '../../hooks/useZoom/useZoom'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { ZoomSelfView } from '../zoomSelfView/ZoomSelfView'

const Container = styled.View(({ theme }) => ({
  gap: theme.spacing['24px'],
  height: '100%',
  width: '100%',
  position: 'relative',
  alignItems: 'center',
  padding: theme.breakpoints.isMobileSized ? theme.spacing['16px'] : theme.spacing['24px'],
}))

const ScrollContainer = styled(ScrollView)({
  flex: 1,
  width: '100%',
})

const BottomBar = styled(BlurView)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  alignItems: 'center',
  paddingVertical: theme.spacing['16px'],
  paddingHorizontal: theme.spacing['16px'],
}))

const TextContainer = styled.View(({ theme }) => ({
  gap: theme.spacing['16px'],
  alignItems: 'center',
  width: '100%',
  marginBottom: theme.spacing['24px'],
}))

const VideoContainer = styled.View({
  width: '100%',
  maxWidth: '600px',
  aspectRatio: '16 / 9',
  borderRadius: '16px',
  position: 'relative',
  overflow: 'hidden',
})

const JoinButton = styled(PrimaryButton)({
  alignSelf: 'center',
  width: '100%',
  maxWidth: '320px',
})

const ButtonsContainer = styled.View(({ theme }) => ({
  paddingTop: theme.spacing['24px'],
  gap: theme.spacing['16px'],
  alignItems: 'center',
  flexDirection: 'row',
  marginBottom: theme.spacing['24px'],
}))

const SessionDetailsContainer = styled.View(({ theme }) => ({
  gap: theme.spacing['4px'],
  alignItems: 'center',
}))

const CloseButton = styled(TertiaryIconButton)(({ theme }) => ({
  alignSelf: 'flex-end',
  marginBottom: theme.spacing['16px'],
}))

export type ZoomVideoCheckProps = {
  getSessionParams: () => Promise<JoinSessionParams>
  participantName: string
  sessionStartDate: Date
  sessionDuration: number
  sessionNumber?: number
  openSettings?: () => void
  isFocused?: boolean
  hideSessionNumber?: boolean
}

export const ZoomVideoCheck: FunctionComponent<ZoomVideoCheckProps> = ({
  getSessionParams,
  participantName,
  sessionStartDate,
  sessionDuration,
  sessionNumber,
  openSettings,
  isFocused,
  hideSessionNumber = false,
}) => {
  const {
    joinSession,
    toggleMute,
    toggleVideo,
    muted,
    videoOff,
    leaveSession,
    config: { shouldEndSessionOnLeave },
  } = useZoom()
  const theme = useTheme() as ThemeType
  const [bottomBarHeight, setBottomBarHeight] = useState(80)
  const [isJoinButtonDisabled, setIsJoinButtonDisabled] = useState(false)
  const isMobileSized = theme.breakpoints.isMobileSized
  const zoomVideoControlsColors = theme.colors.components.zoomVideoControls

  const sessionEndDate = useMemo(
    () => addMinutes(sessionStartDate, sessionDuration),
    [sessionStartDate, sessionDuration],
  )
  return (
    <Container>
      <ScrollContainer
        contentContainerStyle={{
          flexGrow: 1,
          width: '100%',
          gap: theme.spacing['24px'],
          alignItems: 'center',
          paddingBottom: bottomBarHeight,
        }}
      >
        {isMobileSized && (
          <CloseButton
            iconColor={theme.colors.iconDefault}
            onPress={() => leaveSession({ end: shouldEndSessionOnLeave })}
            leftIcon={<CloseIcon size={20} />}
          />
        )}
        <TextContainer>
          <Title
            text={<FormattedMessage defaultMessage='Ready?' description='Title for video check' />}
            size={TitleSize.LARGE}
          />
          <Title
            textAlign='center'
            text={
              isMobileSized ? (
                <FormattedMessage
                  defaultMessage='Find a private spot with good connection. Headphones are recommended.'
                  description='Subtitle for video check'
                />
              ) : (
                <FormattedMessage
                  defaultMessage='Find a private spot with good connection. Headphones are recommended. Close other tabs and exit unnecessary applications to ensure the best video experience'
                  description='Subtitle for video check'
                />
              )
            }
            size={TitleSize.SMALL}
            level='3'
          />
        </TextContainer>
        <VideoContainer testID={tID('ZoomVideoCheck-self-view')}>
          <ZoomSelfView isFullscreen isFocused={isFocused} />
        </VideoContainer>
        {isMobileSized && (
          <>
            <ButtonsContainer>
              <MicVideoControlButton
                size={64}
                onPress={toggleMute}
                disabled={muted}
                enabledColor={zoomVideoControlsColors.enabled.background}
                disabledColor={zoomVideoControlsColors.disabled.background}
                iconColor={zoomVideoControlsColors.icon.text}
              />
              <CameraVideoControlButton
                size={64}
                onPress={toggleVideo}
                disabled={videoOff}
                enabledColor={zoomVideoControlsColors.enabled.background}
                disabledColor={zoomVideoControlsColors.disabled.background}
                iconColor={zoomVideoControlsColors.icon.text}
              />
              {Platform.OS === 'web' && (
                <SettingsVideoControlButton
                  size={64}
                  onPress={() => openSettings?.()}
                  disabled={false}
                  enabledColor={zoomVideoControlsColors.enabled.background}
                  iconColor={zoomVideoControlsColors.icon.text}
                />
              )}
            </ButtonsContainer>
            <SessionDetailsContainer testID={tID('video-session-info')}>
              <BodyText
                text={
                  hideSessionNumber ? (
                    participantName
                  ) : (
                    <FormattedMessage
                      defaultMessage='{participantName} • Session {sessionNumber}'
                      description='Title of video session with participant name and session number'
                      values={{ participantName, sessionNumber }}
                    />
                  )
                }
              />
              <BodyText
                wrap
                size={BodyTextSize.SMALL}
                text={
                  <FormattedDateTimeRange
                    from={sessionStartDate}
                    to={sessionEndDate}
                    hour='numeric'
                    minute='numeric'
                    timeZoneName='short'
                  />
                }
              />
            </SessionDetailsContainer>
          </>
        )}
      </ScrollContainer>
      <BottomBar
        tint='dark'
        intensity={10}
        onLayout={(event: LayoutChangeEvent) => setBottomBarHeight(event.nativeEvent.layout.height)}
      >
        <JoinButton
          testID={tID('ZoomVideoCheck-join-button')}
          text={<FormattedMessage defaultMessage='Join now' description='Button to join video session' />}
          onPress={() => {
            setIsJoinButtonDisabled(true)
            getSessionParams().then((sessionParams) => joinSession(sessionParams))
          }}
          disabled={isJoinButtonDisabled}
          fullWidth
          loading={isJoinButtonDisabled}
          modifier={theme.isAuroraTheme ? ButtonModifier.COBALT_LIGHT : undefined}
        />
      </BottomBar>
    </Container>
  )
}
